import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Field } from './fieldset';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode
} from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';

// Wrapper component for consistent layout
const AuthLayout = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-azul-surelia to-azul-surelia/80 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-verde-lima rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-cian-digital rounded-full blur-3xl"></div>
    </div>
    <div className="relative z-10 w-full max-w-md px-4">
      <div className="text-center mb-8">
        <img src='/logo-blanco-verde.png' alt="Surelia" className="w-auto h-20 mx-auto mb-4" />
      </div>
      <div className="p-8 bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl border border-azul-surelia/20">
        {children}
      </div>
    </div>
  </div>
);

// Password Reset Component
const PasswordReset = ({ oobCode }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setEmail(email);
        setValidating(false);
      })
      .catch((err) => {
        console.error('Invalid reset code:', err.code);
        setInvalid(true);
        setValidating(false);
      });
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err.code, err.message);
      if (err.code === 'auth/expired-action-code') {
        setError('El enlace ha expirado. Solicita uno nuevo.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('El enlace no es válido. Solicita uno nuevo.');
      } else if (err.code === 'auth/weak-password') {
        setError('La contraseña es muy débil. Usa al menos 6 caracteres.');
      } else {
        setError('Error al cambiar la contraseña. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <AuthLayout>
        <div className="text-center">
          <p className="text-azul-surelia">Verificando enlace...</p>
        </div>
      </AuthLayout>
    );
  }

  if (invalid) {
    return (
      <AuthLayout>
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-azul-surelia mb-4">Enlace Inválido</h3>
          <p className="text-sm text-azul-surelia/70">
            Este enlace ha expirado o no es válido. Por favor solicita un nuevo enlace de recuperación.
          </p>
        </div>
        <Button color="surelia" className="w-full" onClick={() => navigate('/forgot-password')}>
          Solicitar Nuevo Enlace
        </Button>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-azul-surelia mb-4">Contraseña Actualizada</h3>
          <p className="text-sm text-azul-surelia/70">
            Tu contraseña ha sido cambiada exitosamente. Ya puedes iniciar sesión.
          </p>
        </div>
        <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
          Iniciar Sesión
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-azul-surelia">Nueva Contraseña</h3>
        {email && (
          <p className="text-sm text-azul-surelia/60 mt-2">{email}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field>
          <label htmlFor="password" className="text-sm font-medium text-azul-surelia">
            Nueva contraseña
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            required
          />
        </Field>
        <Field>
          <label htmlFor="confirmPassword" className="text-sm font-medium text-azul-surelia">
            Confirmar contraseña
          </label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repite la contraseña"
            required
          />
        </Field>
        <div className="pt-2">
          <Button type="submit" color="surelia" className="w-full" disabled={loading}>
            {loading ? 'Guardando...' : 'Cambiar Contraseña'}
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>

      <div className="mt-4 text-center">
        <a href="/login" className="text-sm text-azul-surelia/70 hover:text-azul-surelia hover:underline font-medium">
          Regresar al Inicio de Sesión
        </a>
      </div>
    </AuthLayout>
  );
};

// Email Verification Component
const EmailVerification = ({ oobCode }) => {
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    applyActionCode(auth, oobCode)
      .then(() => {
        setSuccess(true);
        setVerifying(false);
      })
      .catch((err) => {
        console.error('Email verification error:', err.code);
        if (err.code === 'auth/expired-action-code') {
          setError('El enlace ha expirado.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('El enlace no es válido o ya fue utilizado.');
        } else {
          setError('Error al verificar el correo.');
        }
        setVerifying(false);
      });
  }, [oobCode]);

  if (verifying) {
    return (
      <AuthLayout>
        <div className="text-center">
          <p className="text-azul-surelia">Verificando correo electrónico...</p>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-azul-surelia mb-4">Correo Verificado</h3>
          <p className="text-sm text-azul-surelia/70">
            Tu correo electrónico ha sido verificado exitosamente.
          </p>
        </div>
        <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
          Iniciar Sesión
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-azul-surelia mb-4">Error de Verificación</h3>
        <p className="text-sm text-azul-surelia/70">{error}</p>
      </div>
      <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
        Ir al Inicio de Sesión
      </Button>
    </AuthLayout>
  );
};

// Email Recovery Component (when user changes email and wants to revert)
const EmailRecovery = ({ oobCode }) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [restoredEmail, setRestoredEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    checkActionCode(auth, oobCode)
      .then((info) => {
        setRestoredEmail(info.data.email);
        return applyActionCode(auth, oobCode);
      })
      .then(() => {
        setSuccess(true);
        setProcessing(false);
      })
      .catch((err) => {
        console.error('Email recovery error:', err.code);
        if (err.code === 'auth/expired-action-code') {
          setError('El enlace ha expirado.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('El enlace no es válido o ya fue utilizado.');
        } else {
          setError('Error al recuperar el correo.');
        }
        setProcessing(false);
      });
  }, [oobCode]);

  if (processing) {
    return (
      <AuthLayout>
        <div className="text-center">
          <p className="text-azul-surelia">Recuperando correo electrónico...</p>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-azul-surelia mb-4">Correo Recuperado</h3>
          <p className="text-sm text-azul-surelia/70">
            Tu correo ha sido restaurado a <strong>{restoredEmail}</strong>.
          </p>
          <p className="text-sm text-azul-surelia/70 mt-2">
            Si no realizaste este cambio, te recomendamos cambiar tu contraseña.
          </p>
        </div>
        <div className="space-y-2">
          <Button color="surelia" className="w-full" onClick={() => navigate('/forgot-password')}>
            Cambiar Contraseña
          </Button>
          <Button color="white" className="w-full" onClick={() => navigate('/login')}>
            Iniciar Sesión
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-azul-surelia mb-4">Error</h3>
        <p className="text-sm text-azul-surelia/70">{error}</p>
      </div>
      <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
        Ir al Inicio de Sesión
      </Button>
    </AuthLayout>
  );
};

// Main AuthAction Component - Routes to appropriate handler based on mode
const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // No code provided
  if (!oobCode) {
    return (
      <AuthLayout>
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-azul-surelia mb-4">Enlace Inválido</h3>
          <p className="text-sm text-azul-surelia/70">
            El enlace que utilizaste no es válido.
          </p>
        </div>
        <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
          Ir al Inicio de Sesión
        </Button>
      </AuthLayout>
    );
  }

  // Route based on mode
  switch (mode) {
    case 'resetPassword':
      return <PasswordReset oobCode={oobCode} />;
    case 'verifyEmail':
      return <EmailVerification oobCode={oobCode} />;
    case 'recoverEmail':
      return <EmailRecovery oobCode={oobCode} />;
    default:
      return (
        <AuthLayout>
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-azul-surelia mb-4">Acción No Soportada</h3>
            <p className="text-sm text-azul-surelia/70">
              Esta acción no está soportada actualmente.
            </p>
          </div>
          <Button color="surelia" className="w-full" onClick={() => navigate('/login')}>
            Ir al Inicio de Sesión
          </Button>
        </AuthLayout>
      );
  }
};

export default AuthAction;
